import { useMutation, useQuery } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';

import {
  acceptAffiliateRules,
  getAddOnsInfo,
  getAiReviewSummary,
  getCommentList,
  getDownloadLink,
  getQuestionList,
  getSingleProduct,
  postComment,
  postFavorites,
  postQuestion,
  postRate,
  postVote,
} from '@/components/app/product/services';
import {
  GetAddOnsInfoType,
  GetCommentListType,
  GetDownloadLinkType,
  GetProductType,
  GetQuestionListType,
  PostCommentType,
  PostFavoritesType,
  PostQuestionType,
  PostRateType,
  PostVoteType,
} from '@/components/app/product/services/type';

import {
  GET_ACCEPT_AFFILIATE_RULES,
  GET_ADD_TO_CART_INFO,
  GET_AI_REVIEW_SUMMARY,
  GET_COMMENT_LIST,
  GET_DOWNLOAD_LINK,
  GET_QUESTION_LIST,
  GET_SINGLE_PRODUCT,
  POST_COMMENT,
  POST_FAVORITES,
  POST_QUESTION,
  POST_RATE,
  POST_VOTE,
} from '@/constant/query-key';

export const getSingleProductOptions = (slug?: GetProductType) =>
  queryOptions({
    queryKey: [GET_SINGLE_PRODUCT, slug],
    queryFn: () => getSingleProduct(slug),
  });
export const getSingleProductCommentsOptions = ({ slug, filters, page }: GetCommentListType) =>
  queryOptions({
    queryKey: [GET_COMMENT_LIST, slug, filters, page],
    queryFn: () => getCommentList({ slug, filters, page }),
  });
export function useGetSingleProduct(slug?: GetProductType) {
  return useQuery({
    queryKey: [GET_SINGLE_PRODUCT, slug],
    queryFn: () => getSingleProduct(slug),
  });
}

// Runs on the client only (`enabled` is gated on auth) so the token is attached and the API can
// return `currentUserVote`. The product page's own query is prefetched on the server without a
// token — and its response is shared/ISR-cached — so it can never carry per-user vote state.
export function useGetAiReviewSummary({
  slug,
  enabled = true,
}: {
  slug?: GetProductType;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [GET_AI_REVIEW_SUMMARY, slug],
    queryFn: () => getAiReviewSummary(slug),
    enabled: !!slug && enabled,
  });
}

export function useGetCommentList({ slug, filters, page }: GetCommentListType) {
  return useQuery({
    queryKey: [GET_COMMENT_LIST, slug, filters, page],
    queryFn: () => getCommentList({ slug, filters, page }),
  });
}

export function useGetQuestionList({ slug, filters, page }: GetQuestionListType) {
  return useQuery({
    queryKey: [GET_QUESTION_LIST, slug, filters, page],
    queryFn: () => getQuestionList({ slug, filters, page }),
  });
}

export function useGetAddOnsInfo(params: GetAddOnsInfoType) {
  return useQuery({
    queryKey: [GET_ADD_TO_CART_INFO, params],
    queryFn: () => getAddOnsInfo(params),
  });
}

export function useGetDownloadLink(id: GetDownloadLinkType) {
  return useQuery({
    queryKey: [GET_DOWNLOAD_LINK, id],
    queryFn: () => getDownloadLink(id),
    enabled: false,
  });
}

export function usePostComment() {
  return useMutation({
    mutationKey: [POST_COMMENT],
    mutationFn: (body: PostCommentType) => postComment(body),
  });
}
export function usePostQuestion() {
  return useMutation({
    mutationKey: [POST_QUESTION],
    mutationFn: (body: PostQuestionType) => postQuestion(body),
  });
}

export function useGetAcceptAffiliateRules() {
  return useQuery({
    queryKey: [GET_ACCEPT_AFFILIATE_RULES],
    queryFn: acceptAffiliateRules,
    enabled: false,
    gcTime: 0,
  });
}

export function usePostFavorites() {
  return useMutation({
    mutationKey: [POST_FAVORITES],
    mutationFn: (body: PostFavoritesType) => postFavorites(body),
  });
}

export function usePostRate() {
  return useMutation({
    mutationKey: [POST_RATE],
    mutationFn: (body: PostRateType) => postRate(body),
  });
}

export function usePostVote() {
  return useMutation({
    mutationKey: [POST_VOTE],
    mutationFn: (body: PostVoteType) => postVote(body),
  });
}
